import React, { useState, useEffect } from 'react';
import Modal from '../UI/Modal';

interface SaveStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  currentName: string;
  existingNames: string[];
}

const SaveStrategyModal: React.FC<SaveStrategyModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentName,
  existingNames
}) => {
  const [name, setName] = useState<string>(currentName);
  const [error, setError] = useState<string>('');
  const [confirmOverwrite, setConfirmOverwrite] = useState<boolean>(false);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setError('');
      setConfirmOverwrite(false);
    }
  }, [isOpen, currentName]);
  
  const handleSave = () => {
    // Validate name
    if (!name.trim()) {
      setError('Please enter a name for your strategy');
      return;
    }
    
    // Check if name already exists
    if (existingNames.includes(name) && !confirmOverwrite) {
      setError(`A strategy with name "${name}" already exists.`);
      setConfirmOverwrite(true);
      return;
    }
    
    // Proceed with saving
    onSave(name);
    onClose();
  };
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Save Strategy">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Strategy Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
              setConfirmOverwrite(false);
            }}
            placeholder="Enter strategy name"
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
          />
          {error && <p className="mt-1 text-red-400 text-sm">{error}</p>}
          {confirmOverwrite && (
            <p className="mt-1 text-yellow-400 text-sm">
              Click Save again to overwrite the existing strategy.
            </p>
          )}
        </div>
        
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded"
          >
            {confirmOverwrite ? 'Overwrite' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SaveStrategyModal;